/* eslint-disable react-hooks/rules-of-hooks */

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';
import * as unescaper from 'lodash.unescape';
import { createUseStyles } from 'react-jss';
import { createSurveyStyles, StaticContext } from '@sm/webassets';
import { sanitizeString } from '@sm/utils';
import { t, defineMessages } from '@sm/intl';
import { getSurveyQuizFeedback } from './quizScore.graphql';
import QuizResultScore from '../QuizResultScore';

const useThemeStyles = createUseStyles(theme => {
  return {
    qzScoreSummaryContainer: {
      padding: [20, 0, 0],
      margin: [0, 40],
    },
    qzScoreSummaryFeedback: {
      margin: [0, 0, theme?.spacing[6]],
      padding: [theme?.spacing[5], theme?.spacing[6]],
      fontSize: theme?.spacing[5],
      fontWeight: theme?.type.fontWeight.regular,
      background: '#fff',
      borderRadius: theme?.spacing[2],
      boxShadow: '0 0 18 0 #565656',
    },
    qzScoreSummaryText: {
      // custom-text-container
      wordBreak: 'break-word',
      padding: [theme?.spacing[5], theme?.spacing[6], 0],
      boxSizing: 'border-box',
      color: '#000',
    },
    qzScoreSummaryImgContainer: {
      // custom-img-container
      textAlign: 'center',
    },
    qzScoreSummaryImage: {
      maxHeight: 300,
      maxWidth: '100%',
    },
    qzScoreSummaryScore: {
      fontSize: 36,
      fontWeight: 700,
      textAlign: 'center',
    },
    qzScoreSummaryPoints: {
      fontSize: 18,
      padding: [theme?.spacing[2], 0],
      fontWeight: 700,
      textAlign: 'center',
    },
  };
});

const useStyles = createSurveyStyles(theme => {
  return {
    qzScoreSummary: {
      ...theme?.surveyTitle,
      color: theme?.titleColor || theme?.surveyTitle?.color,
      fontFamily: theme?.questionBody?.fontFamily,
    },
  };
});

const DEFAULT_COPY = defineMessages({
  POINTS: {
    id: 'QuizScoreSummary.Points',
    defaultMessage: 'points',
    description: '[Type: Button][Vis.: low] - Points text',
  },
  SCORE: {
    id: 'QuizScoreSummary.Score',
    defaultMessage: 'Score',
    description: '[Type: Text][Vis.: low] - Score text',
  },
});

const getScorePoints = ({ score, maxScore }) => {
  const { qzScoreSummaryPoints } = useThemeStyles();
  if ((score && maxScore) || score === 0) {
    return (
      <div className={`${qzScoreSummaryPoints} sm-qz-score-summary--points`}>
        <span>
          {score}/{maxScore} <QuizResultScore type="points" />
        </span>
      </div>
    );
  }
  return null;
};

const getScorePercent = percentScore => {
  const { qzScoreSummaryScore } = useThemeStyles();
  if (percentScore === 0 || percentScore) {
    return (
      <div className={`${qzScoreSummaryScore} sm-qz-score-summary--score`}>
        {t(DEFAULT_COPY.SCORE)}: {percentScore}%
      </div>
    );
  }
  return null;
};

const getScoreFeedback = (feedback, feedbackImageUrl) => {
  const { qzScoreSummaryFeedback, qzScoreSummaryText, qzScoreSummaryImgContainer, qzScoreSummaryImage } =
    useThemeStyles();
  return (
    <>
      {(feedback || feedbackImageUrl) && (
        <div
          className={`${qzScoreSummaryFeedback} sm-qz-score-summary--feedback`}
          data-testid="QuizScoreSummary__Feedback"
        >
          {feedbackImageUrl && (
            <div className={qzScoreSummaryImgContainer}>
              <img src={feedbackImageUrl} alt={feedback || 'Feedback'} className={qzScoreSummaryImage} />
            </div>
          )}
          {feedback && (
            <div
              className={`${qzScoreSummaryText} sm-qz-score-summary--text`}
              dangerouslySetInnerHTML={{ __html: sanitizeString(unescaper(feedback)) }}
            />
          )}
        </div>
      )}
    </>
  );
};

const QuizScoreSummary = ({ score, surveyId, base }) => {
  const { qzScoreSummaryContainer } = useThemeStyles();
  const { qzScoreSummary } = useStyles();
  const qzScore = base === 'percentage' ? `${score.percent}` : `${score.score}`;
  const { sm } = useContext(StaticContext);
  const qzResultsHeaders = {
    'X-SM-PARAM': sm?.smSlug || '',
  };
  const {
    loading: loadingFeedback,
    error: errorFeedback,
    data: feedbackData,
  } = useQuery(getSurveyQuizFeedback, {
    context: {
      targetPath: '/lo-graphql/quizResults',
      headers: qzResultsHeaders,
    },
    variables: {
      surveyId,
      score: qzScore,
    },
  });

  if (errorFeedback) {
    throw errorFeedback;
  }

  if (!loadingFeedback && !feedbackData) {
    return null;
  }

  const percentScore = score?.percent === 0 || score?.percent ? Math.round(score?.percent) : '';
  const message = feedbackData?.surveyQuizFeedback?.message;
  const imageUrl = feedbackData?.surveyQuizFeedback?.image?.url;

  return (
    <div className={`${qzScoreSummaryContainer} ${qzScoreSummary}`} data-testid="QuizScoreSummary__Container">
      {getScoreFeedback(message, imageUrl)}
      {getScorePercent(percentScore)}
      {getScorePoints(score)}
    </div>
  );
};

QuizScoreSummary.propTypes = {
  score: PropTypes.shape({
    feedback: PropTypes.string,
    score: PropTypes.number,
    maxScore: PropTypes.number,
    percent: PropTypes.number,
  }),
  surveyId: PropTypes.string.isRequired,
  base: PropTypes.string,
};

QuizScoreSummary.defaultProps = {
  score: {},
  base: 'percentage',
};

export default QuizScoreSummary;
